import React from 'react';
import { MapPin, Phone, Clock, Award, Users, Target } from 'lucide-react';
import { businessInfo } from '../mockData';
import { Card, CardContent } from '../components/ui/card';

const About = () => {
  const values = [
    {
      icon: Award,
      title: 'Kvalitet',
      description: 'Vi bruker kun originale og høykvalitets deler i alle våre reparasjoner.'
    },
    {
      icon: Users,
      title: 'Kundefokus',
      description: 'Dine behov kommer først. Vi jobber for å gi deg den beste opplevelsen.'
    },
    {
      icon: Target,
      title: 'Presisjon',
      description: 'Erfarne teknikere som tar seg av enheten din med største omhu.'
    }
  ];

  return (
    <div className="min-h-screen pt-32 pb-16">
      {/* Hero */}
      <section className="bg-gradient-to-br from-sky-50 to-cyan-50 py-16 mb-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Om <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-600 to-cyan-600">DrMobil Strommen</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Din pålitelige partner for reparasjon av mobil, PC, og nettbrett i Strommen
          </p>
        </div>
      </section>

      <div className="container mx-auto px-4">
        {/* About Content */}
        <div className="grid md:grid-cols-2 gap-12 items-center mb-20">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Hvem er vi?
            </h2>
            <div className="space-y-4 text-gray-600 leading-relaxed">
              <p>
                DrMobil Strommen er din lokale ekspert på reparasjon av mobiltelefoner, PC-er, 
                laptops og nettbrett. Vi er lokalisert i Strommen storsenter og tilbyr rask, 
                profesjonell service av høyeste kvalitet.
              </p>
              <p>
                Med erfarne teknikere og moderne utstyr, kan vi håndtere alt fra enkle 
                skjermbytter til komplekse hovedkortreparasjoner. Vi er stolte av å tilby 
                2 års garanti på alle våre reparasjoner og deler.
              </p>
              <p>
                Vårt mål er å gjøre reparasjonsprosessen så enkel og rask som mulig for deg. 
                De fleste reparasjoner gjennomføres mens du venter, slik at du kan fortsette 
                dagen din uten unødvendige avbrudd.
              </p>
            </div>
          </div>

          <div className="relative h-[400px] rounded-2xl overflow-hidden shadow-xl">
            <img
              src="https://images.pexels.com/photos/6755053/pexels-photo-6755053.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Repair workshop"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
          </div>
        </div>

        {/* Values */}
        <section className="mb-20">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Våre verdier
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <Card key={index} className="text-center hover:shadow-xl transition-all hover:-translate-y-2 border-2">
                  <CardContent className="p-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-sky-100 to-cyan-100 rounded-2xl mb-4">
                      <Icon size={32} className="text-sky-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                    <p className="text-gray-600">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>

        {/* Location & Hours */}
        <section className="grid md:grid-cols-2 gap-8">
          {/* Location */}
          <Card className="border-2">
            <CardContent className="p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="bg-gradient-to-br from-sky-100 to-cyan-100 p-3 rounded-xl">
                  <MapPin size={24} className="text-sky-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Vår lokasjon</h3>
                  <p className="text-gray-600">{businessInfo.address}</p>
                  <p className="text-gray-600">{businessInfo.location}</p>
                </div>
              </div>
              <div className="aspect-video bg-gray-100 rounded-xl overflow-hidden">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2000.3!2d10.953!3d59.919!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2sStoperiveien%205%2C%202010%20Strommen%2C%20Norge!5e0!3m2!1sen!2sno!4v1"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  title="DrMobil Strommen Location"
                ></iframe>
              </div>
            </CardContent>
          </Card>

          {/* Opening Hours */}
          <Card className="border-2">
            <CardContent className="p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="bg-gradient-to-br from-sky-100 to-cyan-100 p-3 rounded-xl">
                  <Clock size={24} className="text-sky-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Åpningstider</h3>
                  <p className="text-gray-600">Velkommen innom!</p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex justify-between items-center py-3 border-b">
                  <span className="font-semibold text-gray-900">Mandag - Fredag</span>
                  <span className="text-gray-600">{businessInfo.openingHours.weekdays}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b">
                  <span className="font-semibold text-gray-900">Lørdag</span>
                  <span className="text-gray-600">{businessInfo.openingHours.saturday}</span>
                </div>
                <div className="flex justify-between items-center py-3">
                  <span className="font-semibold text-gray-900">Søndag</span>
                  <span className="text-gray-600">{businessInfo.openingHours.sunday}</span>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t">
                <div className="flex items-center gap-3 mb-4">
                  <Phone size={20} className="text-sky-600" />
                  <div>
                    <div className="text-sm text-gray-500">Ring oss</div>
                    <a href={`tel:${businessInfo.phone}`} className="font-semibold text-gray-900 hover:text-sky-600 transition-colors">
                      {businessInfo.phone}
                    </a>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>
      </div>
    </div>
  );
};

export default About;
